"use client";

import {
  ArrowDown,
  ArrowRight,
  Captions,
  CircleDot,
  MessageCircleQuestion,
  Scan,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";

import { BRAND_CYAN, Container } from "./shared";

type SkillNote = { from: string; to: string; why: string };

type SkillDef = {
  n: string;
  name: SkillKey;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  tagline: string;
  prompt: string;
  inputVisual: "sneaker" | "kitchen" | "construction" | "ui";
  metric: { primary: string; secondary: string };
  notes?: SkillNote[];
};

type SkillKey = "Query" | "Caption" | "Detect" | "Point";

const skillsData: SkillDef[] = [
  {
    n: "01",
    name: "Query",
    Icon: MessageCircleQuestion,
    tagline:
      "Better answers to questions about your images. Classify by your categories, read your forms, recognize your products.",
    prompt:
      "Return JSON with: category, primary_color, accents, closure, sole_material, use_case.",
    inputVisual: "sneaker",
    metric: {
      primary: "Structured catalog data, every time",
      secondary: "Retail, Merchandising, Inventory, Search",
    },
    notes: [
      {
        from: "Sneakers",
        to: "running shoe",
        why: "Corrects category to the taxonomy your catalog actually uses. Generic labels become the specific class your team merchandises against.",
      },
      {
        from: "Off-white",
        to: "white",
        why: "Reflects retail color conventions, where customers shop and filter by the family name, not the literal pixel value.",
      },
      {
        from: "coral",
        to: "orange",
        why: "Snaps fashion descriptors back to the swatch language used across product pages, search facets, and merchandising tags.",
      },
    ],
  },
  {
    n: "02",
    name: "Caption",
    Icon: Captions,
    tagline:
      "Captions in your style and voice. Describe what matters for your use case, skip what doesn't.",
    prompt: "Caption this listing.",
    inputVisual: "kitchen",
    metric: {
      primary: "Same image, your voice",
      secondary: "Retail, Editorial, Marketplaces, SEO",
    },
    notes: [
      {
        from: "modern kitchen",
        to: "Sleek modern kitchen",
        why: "Picks up the editorial register your brand uses on product pages. Descriptive openers instead of bare nouns.",
      },
      {
        from: "dark gray cabinets",
        to: "high-gloss charcoal cabinets",
        why: "Maps observed colors and finishes to the design vocabulary your merchandisers actually write. Finish first, then hue.",
      },
      {
        from: "marble backsplash",
        to: "waterfall quartz island, full-height marble backsplash",
        why: "Learns the materials and architectural features your listings always call out, even when they sit at the edge of frame.",
      },
      {
        from: "tie it all together",
        to: "—",
        why: 'Avoid the cliché closers ("tie it all together", "a true entertainer\'s dream") your style guide bans. Add forbidden phrases to the dataset and the model stops producing them.',
      },
    ],
  },
  {
    n: "03",
    name: "Detect",
    Icon: Scan,
    tagline:
      "Find the objects you care about, ignore the rest. Cut false positives down to near zero.",
    prompt: "Detect: workers without hard hats.",
    inputVisual: "construction",
    metric: {
      primary: "Detect what matters, not just what's there",
      secondary:
        "Manufacturing, Construction, Quality control, Brand protection",
    },
    notes: [
      {
        from: "every person",
        to: "workers without hard hats",
        why: "Learns the negative class specifically. The base model knows what people are. The fine-tune learns which condition matters for your alert.",
      },
      {
        from: "5 boxes, 4 wrong",
        to: "1 box, 0 wrong",
        why: "Cuts false positives by teaching the model the look of compliant workers in your environment: your lighting, your PPE styles, your camera angles.",
      },
      {
        from: "generic confidence",
        to: "production-stable",
        why: "You can set a threshold and trust it. Base-model confidence scores are noisy across scenes; a fine-tune calibrates them against your data.",
      },
    ],
  },
  {
    n: "04",
    name: "Point",
    Icon: CircleDot,
    tagline:
      "More accurate clicks. Better grounding for agents and UI automation.",
    prompt: "Click on the second reel.",
    inputVisual: "ui",
    metric: {
      primary: "Pixel-accurate grounding for agents",
      secondary: "Computer use, UI automation, Accessibility, QA",
    },
    notes: [
      {
        from: "column-wise",
        to: "row-wise",
        why: 'Learns how your users count. "Second reel" means top row, middle column in this app. The base model picks the next-best guess; the fine-tune learns the convention.',
      },
      {
        from: "near-misses",
        to: "center hits",
        why: "Lands the click well inside the target's hit area, not on its edge. Critical for flaky touch targets and tightly packed grids.",
      },
      {
        from: "app-agnostic",
        to: "app-aware",
        why: "Recognizes the layout structure of the specific app. Agents stop wandering through marginal UI affordances and go straight to the intended element.",
      },
    ],
  },
];

const SNEAKER_URL = "https://depot.moondream.ai/ft/lens-page/shoe-horizontal.jpg";
const KITCHEN_URL = "https://depot.moondream.ai/ft/lens-page/kitchen.jpg";
const HARD_HAT_URL = "https://depot.moondream.ai/ft/lens-page/hard_hats_2.jpg";
const INSTAGRAM_URL = "https://depot.moondream.ai/ft/lens-page/instagram.jpg";

const SceneImage = ({ href, slice = "xMidYMid slice" }: { href: string; slice?: string }) => (
  <image
    href={href}
    x="0"
    y="0"
    width="640"
    height="400"
    preserveAspectRatio={slice}
  />
);

const useImageDims = (src: string) => {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);
  return dims;
};

const HARD_HAT_BOXES = [
  { x_min: 0.0, y_min: 0.7334, x_max: 0.0898, y_max: 1.0, correct: false },
  { x_min: 0.0908, y_min: 0.6504, x_max: 0.2471, y_max: 0.998, correct: false },
  { x_min: 0.2627, y_min: 0.5518, x_max: 0.3662, y_max: 0.8311, correct: true },
  { x_min: 0.9314, y_min: 0.6367, x_max: 0.9983, y_max: 0.9414, correct: false },
  { x_min: 0.8779, y_min: 0.5303, x_max: 0.9971, y_max: 0.7979, correct: false },
];

const HardHatBoxes = ({ mode }: { mode: "base" | "fine" }) => {
  const dims = useImageDims(HARD_HAT_URL);
  if (!dims) return null;
  const scale = Math.max(640 / dims.w, 400 / dims.h);
  const dw = dims.w * scale;
  const dh = dims.h * scale;
  const ox = (640 - dw) / 2;
  const oy = 400 - dh;
  const boxes =
    mode === "fine" ? HARD_HAT_BOXES.filter((b) => b.correct) : HARD_HAT_BOXES;
  return (
    <g>
      {boxes.map((b, i) => {
        const x = ox + b.x_min * dw;
        const y = oy + b.y_min * dh;
        const w = (b.x_max - b.x_min) * dw;
        const h = (b.y_max - b.y_min) * dh;
        const color = b.correct ? "#16A34A" : "#DC2626";
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={w}
            height={h}
            fill="none"
            stroke={color}
            strokeWidth="6"
            rx="2"
          />
        );
      })}
    </g>
  );
};

const InstagramDot = ({
  nx,
  ny,
  color,
}: {
  nx: number;
  ny: number;
  color: string;
}) => {
  const dims = useImageDims(INSTAGRAM_URL);
  if (!dims) return null;
  const scale = Math.max(640 / dims.w, 400 / dims.h);
  const dw = dims.w * scale;
  const dh = dims.h * scale;
  const ox = (640 - dw) / 2;
  const oy = (400 - dh) / 2;
  const x = ox + nx * dw;
  const y = oy + ny * dh;
  return (
    <g>
      <circle cx={x} cy={y} r="34" fill={color} opacity="0.22" />
      <circle cx={x} cy={y} r="18" fill={color} opacity="0.7" />
      <circle
        cx={x}
        cy={y}
        r="18"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        opacity="0.85"
      />
    </g>
  );
};

const Scenes = {
  sneaker: () => <SceneImage href={SNEAKER_URL} />,
  kitchen: () => <SceneImage href={KITCHEN_URL} />,
  construction: () => (
    <SceneImage href={HARD_HAT_URL} slice="xMidYMax slice" />
  ),
  ui: () => <SceneImage href={INSTAGRAM_URL} />,
} as const;

type ResultPanelProps = {
  tone: "base" | "fine";
  title: string;
  mono: boolean;
  lines: string[];
};

const ResultPanel = ({ tone, title, mono, lines }: ResultPanelProps) => {
  const fine = tone === "fine";
  return (
    <div
      style={{
        border: fine
          ? "1px solid color-mix(in oklab, #16A34A 32%, transparent)"
          : "1px solid var(--border)",
        background: fine
          ? "color-mix(in oklab, #16A34A 7%, var(--background))"
          : "var(--muted-extra)",
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            background: fine ? "#16A34A" : "#D97706",
          }}
        />
        <span
          style={{
            font: "600 11px/14px var(--font-mono)",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: fine ? "#0a4a26" : "var(--muted-foreground)",
          }}
        >
          {title}
        </span>
      </div>
      <div
        style={{
          font: mono
            ? "400 13px/20px var(--font-mono)"
            : "400 14px/22px var(--font-sans)",
          whiteSpace: mono ? "pre-wrap" : "normal",
          color: "var(--card-foreground)",
        }}
      >
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div>
    </div>
  );
};

type OverlayPair = {
  base: { svg?: () => ReactNode; result?: { mono: boolean; lines: string[] } };
  fine: { svg?: () => ReactNode; result?: { mono: boolean; lines: string[] } };
};

const overlaysFor: Record<SkillKey, OverlayPair> = {
  Query: {
    base: {
      result: {
        mono: true,
        lines: [
          "category:       Sneakers",
          "primary_color:  Off-white",
          "accents:        coral",
          "closure:        lace-up",
          "sole_material:  rubber",
          "use_case:       casual wear",
        ],
      },
    },
    fine: {
      result: {
        mono: true,
        lines: [
          "category:       running shoe",
          "primary_color:  white",
          "accents:        orange",
          "closure:        lace-up",
          "sole_material:  rubber",
          "use_case:       road running",
        ],
      },
    },
  },
  Caption: {
    base: {
      result: {
        mono: false,
        lines: [
          "A modern kitchen features dark gray cabinets, light wood flooring, a stainless steel refrigerator, integrated oven and cooktop, and a marble backsplash.",
        ],
      },
    },
    fine: {
      result: {
        mono: false,
        lines: [
          "Sleek modern kitchen featuring high-gloss charcoal cabinets, a striking waterfall quartz island, and a full-height marble backsplash. Stainless appliances, warm under-cabinet lighting, and wide-plank wood floors tie it all together.",
        ],
      },
    },
  },
  Detect: {
    base: {
      svg: () => <HardHatBoxes mode="base" />,
      result: {
        mono: true,
        lines: [
          "5 boxes returned. 4 are false positives.",
          "Workers wearing hard hats were flagged anyway.",
          "Only 1 detection is correct.",
        ],
      },
    },
    fine: {
      svg: () => <HardHatBoxes mode="fine" />,
      result: {
        mono: true,
        lines: [
          "1 box returned: the single worker without a hard hat.",
          "0 false positives on workers wearing PPE.",
        ],
      },
    },
  },
  Point: {
    base: {
      svg: () => <InstagramDot nx={0.24} ny={0.65} color="#EF4444" />,
      result: {
        mono: true,
        lines: [
          "1 point returned, but it's for the second reel column-wise.",
          "\n",
        ],
      },
    },
    fine: {
      svg: () => <InstagramDot nx={0.5} ny={0.25} color="#16A34A" />,
      result: {
        mono: true,
        lines: ["1 point returned for the correct (row-wise) reel.", "\n"],
      },
    },
  },
};

type CompareFrameProps = {
  label: string;
  sublabel: string;
  Scene: () => ReactNode;
  overlay?: { svg?: () => ReactNode };
  accent: string;
  tone: "base" | "fine";
};

const CompareFrame = ({
  label,
  sublabel,
  Scene,
  overlay,
  accent,
  tone,
}: CompareFrameProps) => (
  <div
    style={{
      position: "relative",
      border:
        tone === "fine"
          ? "1px solid color-mix(in oklab, #16A34A 35%, transparent)"
          : "1px solid var(--border)",
      borderRadius: 12,
      overflow: "hidden",
      background: "var(--muted-extra)",
      boxShadow:
        tone === "fine"
          ? "0 12px 28px -20px color-mix(in oklab, #16A34A 70%, transparent)"
          : "none",
    }}
  >
    <div style={{ aspectRatio: "16 / 10", width: "100%", position: "relative" }}>
      <svg
        viewBox="0 0 640 400"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      >
        <Scene />
        {overlay?.svg ? overlay.svg() : null}
      </svg>
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        background: "var(--card)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <span
        style={{ width: 8, height: 8, borderRadius: 99, background: accent }}
      />
      <span
        style={{
          font: "600 12px/16px var(--font-mono)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--foreground)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          font: "400 12px/16px var(--font-sans)",
          color: "var(--muted-foreground)",
          marginLeft: "auto",
        }}
      >
        {sublabel}
      </span>
    </div>
  </div>
);

const TransitionArrow = () => (
  <div
    aria-hidden="true"
    className="skill-arrow"
    style={{
      position: "absolute",
      top: "calc(50% - 22px)",
      left: "calc(50% - 22px)",
      transform: "translateY(-12px)",
      width: 44,
      height: 44,
      borderRadius: 99,
      background: "var(--background)",
      border: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--foreground)",
      boxShadow: "0 6px 18px -8px rgba(0,0,0,0.18)",
      zIndex: 2,
    }}
  >
    <ArrowRight size={18} strokeWidth={2} />
  </div>
);

const SkillCard = ({ s, isLast }: { s: SkillDef; isLast: boolean }) => {
  const Scene = Scenes[s.inputVisual];
  const ov = overlaysFor[s.name];
  const IconCmp = s.Icon;

  const cardStyle: CSSProperties = {
    padding: "64px 0",
    borderTop: "1px solid var(--border)",
    borderBottom: isLast ? "1px solid var(--border)" : undefined,
    position: "relative",
    background: "var(--background)",
    zIndex: 1,
  };

  return (
    <div className="skill-card" style={cardStyle}>
      <div style={{ position: "relative" }}>
        <div
          className="skill-left-sticky"
          style={{
            position: "sticky",
            top: 96,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          <div
            style={{
              font: "500 13px/16px var(--font-mono)",
              color: "var(--muted-foreground)",
              letterSpacing: "0.04em",
            }}
          >
            {s.n}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--foreground)",
            }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--muted)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--foreground)",
              }}
            >
              <IconCmp size={18} strokeWidth={1.6} />
            </span>
            <span
              style={{
                font: "600 18px/24px var(--font-sans)",
                letterSpacing: "-0.01em",
              }}
            >
              {s.name}
            </span>
          </div>
          <p
            style={{
              font: "400 24px/32px 'Newsreader', Georgia, serif",
              letterSpacing: "-0.4px",
              color: "var(--foreground)",
              margin: 0,
              maxWidth: 340,
            }}
          >
            {s.tagline}
          </p>
          <div
            className="skill-metric"
            style={{
              marginTop: 4,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxWidth: 340,
              color: "var(--muted-foreground)",
              opacity: 0.85,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                font: "400 24px/32px 'Newsreader', Georgia, serif",
                letterSpacing: "-0.4px",
              }}
            >
              {s.metric.primary}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {s.metric.secondary
                .replace(/\.$/, "")
                .split(",")
                .map((t, i) => (
                  <span
                    key={i}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px 10px",
                      border: "1px solid var(--border)",
                      borderRadius: 99,
                      background: "var(--background)",
                      font: "500 12px/16px var(--font-mono)",
                      letterSpacing: "0.02em",
                      color: "var(--muted-foreground)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.trim()}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid var(--border)",
            borderRadius: 10,
            background: "var(--card)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              background: "var(--muted)",
              borderRight: "1px solid var(--border)",
              font: "600 11px/16px var(--font-mono)",
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "var(--muted-foreground)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 99,
                background: BRAND_CYAN,
                display: "inline-block",
              }}
            />
            Prompt
          </div>
          <div
            style={{
              padding: "10px 14px",
              flex: 1,
              font: "400 14px/22px var(--font-mono)",
              color: "var(--foreground)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {s.prompt}
          </div>
          <div
            style={{
              padding: "10px 14px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              color: "var(--muted-foreground)",
              borderLeft: "1px solid var(--border)",
            }}
          >
            <ArrowDown size={16} strokeWidth={2} />
          </div>
        </div>
        <div
          className="skill-compare"
          style={{ position: "relative", alignItems: "stretch" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <CompareFrame
              tone="base"
              label="Before"
              sublabel="Base Moondream"
              Scene={Scene}
              overlay={ov?.base}
              accent="#D97706"
            />
            {ov?.base?.result ? (
              <ResultPanel
                tone="base"
                title="Base Moondream"
                {...ov.base.result}
              />
            ) : null}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <CompareFrame
              tone="fine"
              label="After"
              sublabel="Fine-tuned with Lens"
              Scene={Scene}
              overlay={ov?.fine}
              accent="#16A34A"
            />
            {ov?.fine?.result ? (
              <ResultPanel
                tone="fine"
                title="Fine-tuned with Lens"
                {...ov.fine.result}
              />
            ) : null}
          </div>
          <TransitionArrow />
        </div>

        {s.notes ? (
          <div
            style={{
              padding: "16px 18px",
              border: "1px solid var(--border)",
              borderRadius: 10,
              background: "var(--card)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                font: "600 11px/16px var(--font-mono)",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "var(--muted-foreground)",
              }}
            >
              How it learned
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {s.notes.map((n, i) => (
                <div
                  key={i}
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div
                    style={{
                      font: "500 12px/20px var(--font-mono)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    <span style={{ color: "var(--foreground)" }}>{n.from}</span>
                    <span
                      style={{ margin: "0 6px", color: "var(--muted-foreground)" }}
                    >
                      →
                    </span>
                    <span style={{ color: "#16A34A" }}>{n.to}</span>
                  </div>
                  <div
                    style={{
                      font: "400 14px/20px var(--font-sans)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    {n.why}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const SkillsSection = () => (
  <section style={{ padding: "40px 0 40px" }}>
    <Container>
      <div>
        {skillsData.map((s, i) => (
          <SkillCard
            key={s.n}
            s={s}
            isLast={i === skillsData.length - 1}
          />
        ))}
      </div>
    </Container>
  </section>
);
